import Entity from "_framework/entities/entity";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import Address from "_shared/addresses/address";
import Guid from "_shared/guids/guid";
import EntityAgreementSchema, {
  EntityAgreementJSON,
  EntityAgreementParams,
} from "legal-entities/agreements/_schema/entity-agreement-schema";
import UserOpenEntityAgreement from "legal-entities/agreements/open/user-open-entity-agreement";
import EntityName from "legal-entities/entity-names/entity-name";

export default class EntityAgreement extends Entity<EntityAgreement, EntityAgreementJSON, EntityAgreementParams> {
  public static get TEST_AGREEMENT(): EntityAgreement {
    return new EntityAgreement({
      id: Guid.generate(),
      entityName: EntityName.create("Test Entity Agreement"),
      entityLocation: Address.EXAMPLE,
      termsFileId: Guid.generate(),
    });
  }

  public static create(values: EntityAgreementParams): EntityAgreement {
    const parsed = EntityAgreementSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid EntityAgreement parameters.");
    }
    return new EntityAgreement(parsed);
  }

  public static fromJSON(json: EntityAgreementJSON): EntityAgreement {
    if (isNullOrUndefined(json)) return json;
    const params = EntityAgreementSchema.deserialize(json);
    return new EntityAgreement(params);
  }

  public static fromJSONArray(
    jsonArray: EntityAgreementJSON[] | null | undefined,
  ): EntityAgreement[] | null | undefined {
    if (isNullOrUndefined(jsonArray)) return jsonArray;
    return jsonArray.map((json) => EntityAgreement.fromJSON(json));
  }

  public static fromUserOpenEntityAgreement(userOpenEntityAgreement: UserOpenEntityAgreement) {
    return new EntityAgreement(userOpenEntityAgreement);
  }

  public readonly id: Guid;
  // TODO: Missing "principal: EntityOfficer" field.
  public readonly entityName: EntityName;
  public readonly entityLocation?: Address;
  public readonly termsFileId?: Guid; // TODO: Move into a File typed "Terms" field.$
  public readonly onboardingTemplateId?: string;
  public readonly onboardingTemplateVersion?: string;
  public readonly workflowVersion?: number;
  public readonly acceptedTemplateSnapshot?: unknown;
  // TODO: Missing "status: AgreementStatus" field.

  public constructor(params: EntityAgreementParams, options?: { logSchemaErrors: boolean }) {
    EntityAgreementSchema.validate(params, options?.logSchemaErrors ?? false); // Will throw if invalid.
    super(params);
    this.id = params.id;
    this.entityName = params.entityName;
    this.entityLocation = params.entityLocation;
    this.termsFileId = params.termsFileId;
    this.onboardingTemplateId = params.onboardingTemplateId;
    this.onboardingTemplateVersion = params.onboardingTemplateVersion;
    this.workflowVersion = params.workflowVersion;
    this.acceptedTemplateSnapshot = params.acceptedTemplateSnapshot;
  }

  public toJSON(): EntityAgreementJSON {
    return EntityAgreementSchema.serialize(this);
  }

  public valueOf(): EntityAgreementJSON {
    return this.toJSON();
  }

  public isEqualTo(other: EntityAgreement | null | undefined): boolean {
    return this.id.isEqualTo(other?.id);
  }

  public toString(): string {
    return `EntityAgreement (
      id: ${this.id}
      entityName: ${this.entityName}
      entityLocation: ${this.entityLocation}
      termsFileId: ${this.termsFileId}
    )`;
  }
}
